import React from 'react';
import { useHistory } from 'react-router';
import { Table } from 'react-bootstrap';
import { StyledContainer } from '~components/styled/container';
import dayjs from '~helpers/dayjs';
import { useStoreActions, useStoreState } from '~store/hooks';
import { TableSection } from '~components/styled/table-section';
import { Pagination } from '~components/pagination';
import { LIMIT } from '~store/tags';
import { ApiTags } from '~api/tags/types';
import { Loader } from '~components/loader';
import { settingsPageLinks } from '../links';
import { StyledRow } from './styles';
import { TagRow } from './row';
import { FiltersRow, Filters, filtersToApi } from './filters';
import { throttle } from 'throttle-debounce';

export const Tags: React.FC = () => {
  const { visibility } = useStoreState(({ sideNav }) => sideNav);
  const history = useHistory();
  const [loading, setLoading] = React.useState(true);
  const [tags, setTags] = React.useState<ApiTags[]>([]);
  const { setSideNavModel } = useStoreActions((state) => state.sideNav);
  const { fetchTags } = useStoreActions((state) => state.tags);
  const [totalItems, setTotalItems] = React.useState(10);
  const [offset, setOffset] = React.useState(0);
  const [name, setName] = React.useState('');

  const handleFilterChange = (filters: Partial<Filters>) => {
    setLoading(true);
    const nameChanged = filters.hasOwnProperty('name');
    nameChanged && setName(filters.name);
    // if filters above have been changed reset offset to 0 otherwise to changed offset value
    filters.hasOwnProperty('offset') ? setOffset(filters.offset) : setOffset(0);

    throttle(
      nameChanged ? 250 : 0,
      fetchTags({
        limit: LIMIT,
        ...filtersToApi({ offset, name, ...filters }),
        onSuccess(newTags, newTotalItems) {
          setTags(newTags);
          setTotalItems(newTotalItems);
          setLoading(false);
        }
      })
    );
  };

  const handlePageChange = ({ selected }: { selected: number }) => {
    const newOffset = selected * LIMIT;
    handleFilterChange({ offset: newOffset });
  };

  const deleteTags = (tagId: string) => () => {
    const index = tags.findIndex(({ id }) => id === tagId);
    setTags([...tags.slice(0, index), ...tags.slice(index + 1)]);
  };

  const handleClear = () => {
    setLoading(true);
    setOffset(0);
    setName('');
    history.push({ search: null });

    fetchTags({
      limit: LIMIT,
      onSuccess(newTags, newTotalItems) {
        setTags(newTags);
        setTotalItems(newTotalItems);
        setLoading(false);
      }
    });
  };

  const queryParamsToFilters = () => {
    const filters: Partial<Filters> = {};

    const params = new URLSearchParams(history.location.search) as any;
    for (const [key, value] of params.entries()) {
      switch (key) {
        case 'name':
          filters.name = value;
          setName(filters.name);
          break;
        case 'offset':
          filters.offset = value;
          setOffset(filters.offset);
          break;
        default:
          break;
      }
    }

    return filters;
  };

  React.useLayoutEffect(() => {
    const params: string[] = [];

    name.length && params.push(`name=${name}`);
    !!offset && params.push(`offset=${offset}`);

    if (params.length) history.push({ search: `?${params.join('&')}` });
    else if (history.action !== 'POP') history.push({ search: null });
  }, [offset, name]);

  React.useEffect(() => {
    setSideNavModel({
      topBarLeftTitle: 'Tags',
      activeChild: 'tags',
      links: settingsPageLinks
    });

    fetchTags({
      limit: LIMIT,
      ...filtersToApi(queryParamsToFilters()),
      onSuccess(newTags, newTotalItems) {
        setTags(newTags);
        setTotalItems(newTotalItems);
        setLoading(false);
      }
    });
  }, []);

  return (
    <StyledContainer $sideNavVisible={visibility === 'show'}>
      <FiltersRow name={name} offset={offset} onFilterChange={handleFilterChange} onClear={handleClear} />
      <StyledRow>
        <TableSection>
          <Loader loading={loading} backdrop />
          <Table responsive>
            <thead>
              <tr>
                <th>Id</th>
                <th>Name</th>
                <th style={{ width: '20%' }}>Created At</th>
                <th style={{ width: '8rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tags
                .sort((a, b) => dayjs(b.createdAt).diff(a.createdAt))
                .map((tag) => (
                  <TagRow key={tag.id} tag={tag} onDeleteSuccess={deleteTags(tag.id)} />
                ))}
            </tbody>
          </Table>
          <Pagination
            pageCount={Math.ceil(totalItems / LIMIT)}
            marginPagesDisplayed={5}
            pageRangeDisplayed={4}
            forcePage={offset / LIMIT}
            breakLabel="..."
            onPageChange={handlePageChange}
          />
        </TableSection>
      </StyledRow>
    </StyledContainer>
  );
};
